import { useState, useEffect } from "react";
import FlowMeterChoice from "../../components/FlowMeterChoice";
import FlowMeasurementChoice from "../../components/FlowMeasurementChoice";
import { HiCalculator } from "react-icons/hi";
import BackButton from "../../components/BackButton";
import { parseISO, isBefore } from "date-fns";
import { toast } from "sonner";
import api from "../../api/manage_calculations/JetsonReq";
import Spinner from "../../components/Spinner";
import ParamsInputs from "../../components/ParamsInputs";
import TitleWithReloadButton from "../../components/TitleWithReloadButton";
import { FcOk } from "react-icons/fc";
import { MdCancel } from "react-icons/md";
import Labels from "../../components/Labels";
import CalculationOnParam from "../../components/CalculationOnParam";
import BaseLines from "../../components/BaseLines";
import ThresholdSuggestionTable from "../../components/ThresholdSuggestionTable";

const showError = (message) => toast.error(message, { duration: 30000 });
const showDiagnosticWarning = (message) => toast.warning(message, { duration: 30000 });


const StdPattern = () => {

    const [selectedFlowMeter, setSelectedFlowMeter] = useState('');
    const [dateTimeFrom, setDateTimeFrom] = useState('');
    const [dateTimeTo, setDateTimeTo] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSuggestingThreshold, setIsSuggestingThreshold] = useState(false);
    const [thresholdSuggestion, setThresholdSuggestion] = useState(null);

    const parameters = [
        'Flow',
        'DT',
        'TTUp',
        'TTDn',
        'FlowVelocity',
        'AbsoluteSoundVelocity',
        'SoundVelocity',
        'AbsoluteRe',
        'Re',
        'SignalAmplitudeUp',
        'SignalAmplitudeDn',
        'RxGainUp',
        'RxGainDn',
        'SNRUp',
        'SNRDn',
        'CorrelationCoef',
        'ReferenceCoef',
        'PcbTemp',
        'ExternalInput',
        'BurstsAccepted',
        'Totalizer',
        'Temp',
        'StDev',
        'DigitalInput',

        'MeanFlow',
        'MeanDT',
        'StdFlow',
        'StdDT',
        'DiffStdFlow',

        'DCVoltageElectrode',
        'ElectrodeDiffVoltage',
        'ImpedanceElectrode',
        'ImpedanceElectrodeUnfiltered',
        'RawVelocity',
        'CoilResistance',
        'CoilCurrentStatus',
        'CoilBoostTime',
        'SignalInputSpan',
        'SignalNoiseImmunity',
        'ElectricalConductivity',
    ];

    const notPerTrackParameters = [
        'Flow',
        'Totalizer',
        'AbsoluteSoundVelocity',
        'AbsoluteRe',
        'PcbTemp',
        'ExternalTemp',
        'ExternalInput',
        'MeanFlow',
        'StdFlow',
        // 'DiffStdFlow',
        'ElectrodeDiffVoltage',
        'RawVelocity',
        'CoilResistance',
        'CoilCurrentStatus',
        'CoilBoostTime',
        'SignalInputSpan',
        'SignalNoiseImmunity',
        'ElectricalConductivity',
    ];

    const [selectedParameter, setSelectedParameter] = useState(parameters[0]);

    const initCoefficients = {
        'A': 'Not calculated',
        'B': 'Not calculated',
        'Base': 'Not calculated'
    }

    const [calculatedCoefficients, setCalculatedCoefficients] = useState(initCoefficients);

    const initCheckBoxes = {
        1: true,
        2: false,
        3: false,
        4: false
    }

    const initCheckBox = {
        1: true
    }

    const [checkBoxes, setCheckBoxes] = useState(initCheckBoxes);

    const handleCheckBoxes = (nr, checked) => {
        setCheckBoxes((prev) => ({
            ...prev,
            [nr]: checked
        }))
    }

    const initdata = {
        'treshold': '',
        'cutoff_lev': '',
        'fs': '',
        'butter_order': '',
        'min_section_len_multiplier': '',
        'eps': '',
    }

    const formatData = {
        'treshold': ['any', null],
        'cutoff_lev': ['1', 0],
        'fs': ['any', null],
        'butter_order': ['1', 0],
        'min_section_len_multiplier': ['1', 0],
        'eps': ['any', null],
    }

    const [params, setParams] = useState(initdata);

    const checkParamsEmpty = () => {
        return Object.values(params).some((value) => value === '' || value == null)
    }

    const filterParamsFromResponse = (responseData) => {
        const data = Object.entries(responseData).filter(([key, _]) => key !== "window_len")
        return Object.fromEntries(data)
    }

    const handleSuggestThreshold = async () => {
        const suggestionParamNames = [
            'cutoff_lev',
            'fs',
            'butter_order',
            'min_section_len_multiplier',
            'eps',
        ];
        const missingParam = suggestionParamNames.some(
            (name) => params[name] === '' || params[name] == null
        );
        if (!selectedFlowMeter) {
            showError("Select a flow meter first!");
            return;
        }
        if (missingParam) {
            showError("Fill in all filter params. The treshold field may remain empty.");
            return;
        }

        const dateTimeFromParsed = parseISO(dateTimeFrom);
        const dateTimeToParsed = parseISO(dateTimeTo);
        if (!isBefore(dateTimeFromParsed, dateTimeToParsed)) {
            showError('Error! First datetime is not earlier!');
            return;
        }

        const baseLines = Object.keys(checkBoxes)
            .filter((key) => checkBoxes[key] === true)
            .map((key) => Number(key));
        if (baseLines.length === 0) {
            showError("Select at least one line!");
            return;
        }

        setIsSuggestingThreshold(true);
        setThresholdSuggestion(null);
        try {
            const response = await api.post(
                "suggest-pattern-std-threshold",
                {
                    datetime_from: dateTimeFrom,
                    datetime_to: dateTimeTo,
                    flow_meter_id: selectedFlowMeter,
                    base: selectedParameter,
                    base_lines: baseLines,
                    params: {
                        current_treshold: params.treshold === '' ? null : params.treshold,
                        cutoff_lev: params.cutoff_lev,
                        fs: params.fs,
                        butter_order: params.butter_order,
                        min_section_len_multiplier: params.min_section_len_multiplier,
                        eps: params.eps,
                    },
                },
                { withCredentials: true }
            );
            if (response.data.success) {
                setThresholdSuggestion(response.data.payload);
                toast.success("Threshold suggestions are ready.");
            } else {
                showError(response.data.message);
            }
        } catch (err) {
            console.log(err.response);
            const detail = err.response?.data?.detail;
            const message = err.response?.data?.message
                ?? (Array.isArray(detail)
                    ? detail.map((item) => item.msg).join("; ")
                    : detail)
                ?? "Could not prepare threshold suggestions.";
            showError(message);
        } finally {
            setIsSuggestingThreshold(false);
        }
    };

    const handleAction = async (toastId) => {
        if (checkParamsEmpty()) {
            showError("Fill in all params!");
            return
        }
        setIsLoading(true);
        toast.dismiss(toastId);
        const dateTimeFromParsed = parseISO(dateTimeFrom);
        const dateTimeToParsed = parseISO(dateTimeTo);
        
        if (!isBefore(dateTimeFromParsed, dateTimeToParsed)) {
            showError('Error! First datetime is not earlier!')
            setIsLoading(false);
            return
        }

        const base_lines = Object.keys(checkBoxes).filter(key => checkBoxes[key] === true).map(key => Number(key));
        
        try {
            const response = await api.post(
                "calculate-pattern-std",
                {
                    datetime_from: dateTimeFrom,
                    datetime_to: dateTimeTo,
                    flow_meter_id: selectedFlowMeter,
                    params: params,
                    base: selectedParameter,
                    base_lines: base_lines
                },
                {
                    withCredentials: true
                }
            );
            if (response.data.success) {
                toast.success(response.data.message);
                const warnings = response.data.payload?.warnings ?? [];
                warnings.forEach((warning) => {
                    showDiagnosticWarning(`${warning.message} ${warning.hint}`);
                });
            } else {
                showError(response.data.message);
            }
        } catch (err) {
            console.log(err.response);
            const detail = err.response?.data?.detail;
            const message = err.response?.data?.message
                ?? (Array.isArray(detail)
                    ? detail.map((item) => item.msg).join("; ")
                    : detail)
                ?? "Nie udało się wykonać diagnostyki Std Pattern.";
            showError(message);
        }
        setIsLoading(false);
    }

    const handleDefaultParams = async () => {
        setIsLoading(true);
        try {
            const response = await api.get(
                `default-calculation-params/${selectedFlowMeter}/pattern`,
                {
                    withCredentials: true
                }
            );
            if (response.data.success) {
                setParams(filterParamsFromResponse(response.data.payload));
            } else {
                showError(response.data.message);
            }
        } catch (err) {
            console.log(err.response);
        }
        setIsLoading(false);
    }

    const handleDeleteParams = async (toastId) => {
        setIsLoading(true);
        toast.dismiss(toastId);
        try {
            const response = await api.delete(
                `calculation-params/${selectedFlowMeter}/pattern`,
                {
                    withCredentials: true
                }
            )
            if (response.data.success) {
                setParams(initdata);
                setCalculatedCoefficients(initCoefficients);
                toast.success(response.data.message)
            } else {
                showError(response.data.message)
            }        
        } catch (err) {
            console.log(err.response);
        }
        setIsLoading(false);
    }

    const handleSaveParams = async () => {
        if (checkParamsEmpty()) {
            showError("Fill in all params!");
            return
        }
        setIsLoading(true);
        try {
            const response = await api.post(
                'save-calculation-params-std',
                {
                    flow_meter_id: selectedFlowMeter,
                    params_type: "pattern",
                    params: params
                },
                {
                    withCredentials: true
                }
            )
            if (response.data.success) {
                toast.success(response.data.message)
            }
        } catch (err) {
            console.log(err.response)
            showError("Something go wrong!\nCheck logs!")
        }
        setIsLoading(false);
    }

    useEffect(() => {
        const getCalculationParams = async () => {
            if (!selectedFlowMeter) return; 
            try {
                const response = await api.get(
                    `calculation-params/${selectedFlowMeter}/pattern`,
                    {
                        withCredentials: true
                    }
                )
                if (response.data.success) {
                    setParams(filterParamsFromResponse(response.data.payload));
                } else {
                    setParams(initdata);
                    showError(response.data.message);
                }
                
            } catch (err) {
                console.log(err.response);
            }
        }
        if (selectedFlowMeter !== "")
            getCalculationParams();
    }, [selectedFlowMeter])
    
    useEffect(() => {    
        const getCalculatedCoefficients = async () => {
            if (!selectedFlowMeter) return; 
            try {
                const response = await api.get(
                    `calculation-pattern-std/${selectedFlowMeter}`,
                    {
                        withCredentials: true
                    }
                )
                if (response.data.success) {
                    if (response.data.payload.a === 0 && response.data.payload.b === 0) {
                        setCalculatedCoefficients(initCoefficients);
                    } else {
                        setCalculatedCoefficients(response.data.payload);
                        setSelectedParameter(response.data.payload.base);
                    }                       
                } else {
                    setCalculatedCoefficients(initCoefficients);
                    showError(response.data.message);
                }
                
            } catch (err) {
                console.log(err.response);
            }
        }
        if (selectedFlowMeter !== "")
            getCalculatedCoefficients();
    }, [selectedFlowMeter])
    
    const handleCalculate = () => {
        if (checkParamsEmpty()) {
            showError("Fill in all params!");
            return
        }
        const msg = "Are you sure to calculate std pattern for selected flow meter in given date range?"
        const toastId = toast.warning(msg, {
            style: {width: '20rem'},
            action: <FcOk onClick={() => handleAction(toastId)} style={{fontSize: '4rem'}}/>,
            cancel: <MdCancel onClick={() => handleCancel(toastId)} style={{fontSize: '4rem', color: 'red'}}/>,
            duration: 10000
        })
    }

    const handleCancel = (toastId) => {
        toast.warning(
            'Operation cancelled!',
            {
                id: toastId,
                action: null,
                cancel: null,
                duration: 5000
            }
        );
    }

    const handleDelete = () => {
        const msg = "Are you sure to delete calculate std pattern all params for selected flow meter?"
        const toastId = toast.warning(msg, {
            style: {width: '20rem'},
            action: <FcOk onClick={() => handleDeleteParams(toastId)} style={{fontSize: '4rem'}}/>,
            cancel: <MdCancel onClick={() => handleCancel(toastId)} style={{fontSize: '4rem', color: 'red'}}/>,
            duration: 10000
        })
    }

    useEffect(() => {
        if (notPerTrackParameters.includes(selectedParameter))
            setCheckBoxes(initCheckBox);
        else
            setCheckBoxes(initCheckBoxes)
    }, [selectedParameter])

    const isBusy = isLoading || isSuggestingThreshold;

    return (
        <div className="std-pattern-container">
            <TitleWithReloadButton
                title="Std Pattern"
            />
            <div className="std-pattern-card">
                <Labels
                    title="Calculated Coefficients"
                    data={calculatedCoefficients}
                />
                <FlowMeterChoice 
                    setSelectedFlowMeter={setSelectedFlowMeter}
                    disabled={isBusy}
                />
                <FlowMeasurementChoice 
                    setDateTimeFrom={setDateTimeFrom}
                    setDateTimeTo={setDateTimeTo}
                    disabled={isBusy}
                />
                {isBusy? <button className="std-pattern-button-warning">
                    <Spinner size="small" />
                </button>: <button className="std-pattern-button-warning" onClick={handleDefaultParams}>
                    <div>Set default parameters</div>
                </button>}
                <CalculationOnParam 
                    Parameters={parameters}
                    selectedParameter={selectedParameter}
                    setSelectedParameter={setSelectedParameter}
                    disabled={isBusy}
                />
                <BaseLines
                    data={checkBoxes}
                    setData={handleCheckBoxes}
                />
                <ParamsInputs 
                    data={params}
                    setData={setParams}
                    disabled={isBusy}
                    formatData={formatData}
                />
                <button
                    type="button"
                    className="std-pattern-threshold-suggestion-button"
                    onClick={handleSuggestThreshold}
                    disabled={isBusy}
                >
                    {isSuggestingThreshold
                        ? <Spinner size="small" />
                        : "Suggest treshold"}
                </button>
                <div className="std-pattern-buttons-div">
                    {isBusy? <button className="std-pattern-button std-pattern-button-delete">
                        <Spinner size="small" />
                    </button>: <button className="std-pattern-button std-pattern-button-delete" onClick={handleDelete}>
                        Delete params
                    </button>}
                    {isBusy? <button className="std-pattern-button">
                        <Spinner size="small" />
                    </button>: <button className="std-pattern-button" onClick={handleSaveParams}>
                        Save params
                    </button>}
                </div>
                
                {isBusy? <button className="std-pattern-button">
                    <Spinner size="small" />
                </button>:
                <button className="std-pattern-button" onClick={handleCalculate}>
                    <div>Calculate <HiCalculator /></div>
                </button>}
            </div>
            <ThresholdSuggestionTable
                suggestion={thresholdSuggestion}
                onClose={() => setThresholdSuggestion(null)}
            />
            <BackButton 
                navigateTo='/manage-calculations'
            />
        </div>   
    )
}

export default StdPattern;
